import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import FormattedCurrency from '../../../../../components/formatted-currency';

import { computeAbsoluteEaches } from '../../../../../constants/units-of-measure';
import { useEnableUnitsOfMeasure } from '../../../../../feature-flags/hooks/use-enable-units-of-measure';
import { messages as editCostMessages } from '../edit/purchase-order-cost-summary-messages';
import { costSummaryMessages as previewCostMessages } from './cost-summary.messages';
import styles from './cost-summary.styles.module.less';

type CostSummaryProduct = {
  quantity?: number;
  cost?: number;
  totalReceived?: number;
  unitOfMeasure?: number | null;
  unitsOfMeasure?: { productUnitOfMeasureId: number; conversionFactor: number; parentProductUnitOfMeasureId?: number | null }[];
};

type CostSummaryProps = {
  products: CostSummaryProduct[];
  currencyCode: string;
  shippingCost?: number;
  taxes?: number;
  tariffs?: number;
};

export const CostSummary = ({ products, currencyCode, shippingCost = 0, taxes = 0, tariffs = 0 }: CostSummaryProps) => {
  const intl = useIntl();
  const isUnitsOfMeasureEnabled = useEnableUnitsOfMeasure();

  const orderedCostOfGoods = useMemo(() => {
    return products.reduce((sum, p) => {
      const qty = p.quantity ?? 0;
      const cost = p.cost ?? 0;
      const absoluteEaches =
        isUnitsOfMeasureEnabled && (p.unitOfMeasure ?? 0) > 0 ? computeAbsoluteEaches(p.unitOfMeasure!, p.unitsOfMeasure ?? []) : 1;
      return sum + qty * cost * absoluteEaches;
    }, 0);
  }, [products, isUnitsOfMeasureEnabled]);

  const receivedCostOfGoods = useMemo(() => {
    return products.reduce((sum, p) => sum + (p.totalReceived ?? 0) * (p.cost ?? 0), 0);
  }, [products]);

  const orderedTotalCost = orderedCostOfGoods + shippingCost + taxes + tariffs;
  const receivedTotalCost = receivedCostOfGoods + shippingCost + taxes + tariffs;

  return (
    <div className={styles['cost-summary-container']}>
      <Text className={styles['cost-summary-header']} textType={TextType.H4} value={intl.formatMessage(editCostMessages.costSummary)} />

      <FlexColumn className={styles['cost-summary-values']} gap={Gap.Space4}>
        <FlexRow className={styles['cost-summary-row']} gap={Gap.Space16}>
          <Text
            className={styles['cost-summary-label']}
            textType={TextType.Paragraph}
            value={intl.formatMessage(editCostMessages.costOfGoods)}
          />
          <Text
            className={styles['cost-summary-value']}
            textType={TextType.Paragraph}
            value={<FormattedCurrency currencyCode={currencyCode} value={orderedCostOfGoods} />}
          />
        </FlexRow>
        <FlexRow className={styles['cost-summary-row']} gap={Gap.Space16}>
          <Text
            className={styles['cost-summary-label']}
            textType={TextType.Paragraph}
            value={intl.formatMessage(editCostMessages.totalCost)}
          />
          <Text
            className={styles['cost-summary-value']}
            textType={TextType.Paragraph}
            value={<FormattedCurrency currencyCode={currencyCode} value={orderedTotalCost} />}
          />
        </FlexRow>
      </FlexColumn>

      <div className={styles['received-divider']} />

      <FlexColumn className={styles['cost-summary-values-received']} gap={Gap.Space4}>
        <FlexRow className={styles['cost-summary-row']} gap={Gap.Space16}>
          <Text
            className={styles['cost-summary-label-received']}
            textType={TextType.Paragraph}
            value={intl.formatMessage(previewCostMessages.receivedCostOfGoods)}
          />
          <Text
            className={styles['cost-summary-value-received']}
            textType={TextType.Paragraph}
            value={<FormattedCurrency currencyCode={currencyCode} value={receivedCostOfGoods} />}
          />
        </FlexRow>
        <FlexRow className={styles['cost-summary-row']} gap={Gap.Space16}>
          <Text
            className={styles['cost-summary-label-received']}
            textType={TextType.Paragraph}
            value={intl.formatMessage(previewCostMessages.receivedTotalCost)}
          />
          <Text
            className={styles['cost-summary-value-received']}
            textType={TextType.Paragraph}
            value={<FormattedCurrency currencyCode={currencyCode} value={receivedTotalCost} />}
          />
        </FlexRow>
      </FlexColumn>
    </div>
  );
};
